import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useCartContext } from '../../context/cart';
import { deriveErrorMessage } from '../../util/deriveErrorMessage';
import mergeOperations from '../../util/shallowMerge';
import DEFAULT_OPERATIONS from './miniCart.gql';
import { useEventingContext } from '../../context/eventing';
/**
 *
 * @param {Boolean} props.isOpen - True if the mini cart is open
 * @param {Function} props.setIsOpen - Function to toggle the mini cart
 * @param {DocumentNode} props.operations.miniCartQuery - Query to fetch mini cart data
 * @param {DocumentNode} props.operations.removeItemMutation - Mutation to remove an item from cart
 *
 * @returns {
 *      closeMiniCart: Function,
 *      errorMessage: String,
 *      handleEditCart: Function,
 *      handleProceedToCheckout: Function,
 *      handleRemoveItem: Function,
 *      loading: Boolean,
 *      productList: Array<>,
 *      subTotal: Number,
 *      totalQuantity: Number
 *      configurableThumbnailSource: String
 *  }
 */

export var useMiniCart = function useMiniCart(props) {
  var isOpen = props.isOpen,
      setIsOpen = props.setIsOpen;

  var _useEventingContext = useEventingContext(),
      _useEventingContext2 = _slicedToArray(_useEventingContext, 2),
      dispatch = _useEventingContext2[1].dispatch;

  var operations = mergeOperations(DEFAULT_OPERATIONS, props.operations);
  var removeItemMutation = operations.removeItemMutation,
      miniCartQuery = operations.miniCartQuery,
      getStoreConfigQuery = operations.getStoreConfigQuery;

  var _useCartContext = useCartContext(),
      _useCartContext2 = _slicedToArray(_useCartContext, 1),
      cartId = _useCartContext2[0].cartId;

  var history = useHistory();

  var _useQuery = useQuery(miniCartQuery, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      cartId: cartId
    },
    skip: !cartId,
    errorPolicy: 'all'
  }),
      miniCartData = _useQuery.data,
      miniCartLoading = _useQuery.loading;

  var _useQuery2 = useQuery(getStoreConfigQuery, {
    fetchPolicy: 'cache-and-network'
  }),
      storeConfigData = _useQuery2.data;

  var configurableThumbnailSource = useMemo(function () {
    if (storeConfigData) {
      return storeConfigData.storeConfig.configurable_thumbnail_source;
    }
  }, [storeConfigData]);
  var storeUrlSuffix = useMemo(function () {
    if (storeConfigData) {
      return storeConfigData.storeConfig.product_url_suffix;
    }
  }, [storeConfigData]);

  var _useMutation = useMutation(removeItemMutation),
      _useMutation2 = _slicedToArray(_useMutation, 2),
      removeItem = _useMutation2[0],
      _useMutation2$ = _useMutation2[1],
      removeItemLoading = _useMutation2$.loading,
      removeItemCalled = _useMutation2$.called,
      removeItemError = _useMutation2$.error;

  var totalQuantity = useMemo(function () {
    if (!miniCartLoading) {
      var _miniCartData$cart;

      return miniCartData === null || miniCartData === void 0 ? void 0 : (_miniCartData$cart = miniCartData.cart) === null || _miniCartData$cart === void 0 ? void 0 : _miniCartData$cart.total_quantity;
    }
  }, [miniCartData, miniCartLoading]);
  var subTotal = useMemo(function () {
    if (!miniCartLoading) {
      var _miniCartData$cart2, _miniCartData$cart2$p;

      return miniCartData === null || miniCartData === void 0 ? void 0 : (_miniCartData$cart2 = miniCartData.cart) === null || _miniCartData$cart2 === void 0 ? void 0 : (_miniCartData$cart2$p = _miniCartData$cart2.prices) === null || _miniCartData$cart2$p === void 0 ? void 0 : _miniCartData$cart2$p.subtotal_excluding_tax;
    }
  }, [miniCartData, miniCartLoading]);
  var productList = useMemo(function () {
    if (!miniCartLoading) {
      var _miniCartData$cart3;

      return miniCartData === null || miniCartData === void 0 ? void 0 : (_miniCartData$cart3 = miniCartData.cart) === null || _miniCartData$cart3 === void 0 ? void 0 : _miniCartData$cart3.items;
    }
  }, [miniCartData, miniCartLoading]);
  var closeMiniCart = useCallback(function () {
    setIsOpen(false);
  }, [setIsOpen]);
  var handleRemoveItem = useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(id) {
      var _product$configurable, _productList$filter, _productList$filter2, product, selectedOptionsLabels;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              _context.next = 3;
              return removeItem({
                variables: {
                  cartId: cartId,
                  itemId: id
                }
              });

            case 3:
              _productList$filter = productList.filter(function (p) {
                return (p.uid || p.id) === id;
              }), _productList$filter2 = _slicedToArray(_productList$filter, 1), product = _productList$filter2[0];
              selectedOptionsLabels = ((_product$configurable = product.configurable_options) === null || _product$configurable === void 0 ? void 0 : _product$configurable.map(function (_ref2) {
                var option_label = _ref2.option_label,
                    value_label = _ref2.value_label;
                return {
                  attribute: option_label,
                  value: value_label
                };
              })) || null;
              dispatch({
                type: 'CART_REMOVE_ITEM',
                payload: {
                  cartId: cartId,
                  sku: product.product.sku,
                  name: product.product.name,
                  priceTotal: product.prices.price.value,
                  currencyCode: product.prices.price.currency,
                  discountAmount: product.prices.total_item_discount.value,
                  selectedOptions: selectedOptionsLabels,
                  quantity: product.quantity
                }
              });
              _context.next = 10;
              break;

            case 8:
              _context.prev = 8;
              _context.t0 = _context["catch"](0);

            case 10:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[0, 8]]);
    }));

    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }(), [removeItem, cartId, dispatch, productList]);
  var handleProceedToCheckout = useCallback(function () {
    setIsOpen(false);
    history.push('/checkout');
  }, [history, setIsOpen]);
  var handleEditCart = useCallback(function () {
    setIsOpen(false);
    history.push('/cart');
  }, [history, setIsOpen]);
  var derivedErrorMessage = useMemo(function () {
    return deriveErrorMessage([removeItemError]);
  }, [removeItemError]);
  useEffect(function () {
    if (isOpen) {
      dispatch({
        type: 'MINI_CART_VIEW',
        payload: {
          cartId: cartId,
          products: productList
        }
      });
    }
  }, [isOpen, cartId, productList, dispatch]);
  return {
    closeMiniCart: closeMiniCart,
    errorMessage: derivedErrorMessage,
    handleEditCart: handleEditCart,
    handleProceedToCheckout: handleProceedToCheckout,
    handleRemoveItem: handleRemoveItem,
    loading: miniCartLoading || removeItemCalled && removeItemLoading,
    productList: productList,
    subTotal: subTotal,
    totalQuantity: totalQuantity,
    configurableThumbnailSource: configurableThumbnailSource,
    storeUrlSuffix: storeUrlSuffix
  };
};